import { Link, useParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { BLOG_POSTS } from '../lib/pageContent';
import { BLOG_POST_PAGE } from '../constants/testIds';

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useDocumentMeta(
    post ? `${post.title} | BulkUsedTires.com Blog` : 'Article Not Found | BulkUsedTires.com',
    post ? post.excerpt : undefined,
  );

  if (!post) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <Navbar />
        <div data-testid={BLOG_POST_PAGE.notFound} className="pt-40 pb-24 text-center px-5">
          <p className="font-display font-extrabold uppercase text-brand-text text-3xl">Article Not Found</p>
          <Link to="/blog" className="mt-5 inline-block text-brand-amber underline">Back to Blog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    datePublished: post.date,
    description: post.excerpt,
    author: { '@type': 'Organization', name: 'BulkUsedTires.com' },
  };

  return (
    <div className="min-h-screen bg-brand-bg">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />
      <Navbar />
      <article data-testid={BLOG_POST_PAGE.section} className="pt-32 sm:pt-40 pb-20 sm:pb-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <nav className="font-mono text-xs uppercase tracking-wider text-brand-muted mb-6">
            <Link to="/" className="hover:text-brand-orange transition-colors">Home</Link>
            <span className="mx-2 text-brand-border">/</span>
            <Link to="/blog" className="hover:text-brand-orange transition-colors">Blog</Link>
          </nav>
          <p className="font-mono text-xs uppercase tracking-widest text-brand-amber">
            {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} &middot; {post.readTime}
          </p>
          <h1 className="mt-3 font-display font-extrabold uppercase tracking-tight text-brand-text text-3xl sm:text-4xl lg:text-5xl">
            {post.title}
          </h1>
          <p className="mt-5 text-brand-muted text-base sm:text-lg leading-relaxed">{post.excerpt}</p>

          <div className="mt-10 space-y-9">
            {post.sections.map((sec) => (
              <div key={sec.heading}>
                <h2 className="font-display font-bold uppercase text-brand-text text-xl sm:text-2xl">{sec.heading}</h2>
                {sec.paragraphs.map((p, i) => (
                  <p key={i} className="mt-3 text-brand-muted text-sm sm:text-base leading-relaxed">{p}</p>
                ))}
              </div>
            ))}
          </div>

          <div className="mt-14 rounded-2xl border border-brand-border bg-brand-surface p-7 sm:p-9 text-center">
            <p className="font-display font-bold uppercase text-brand-text text-xl sm:text-2xl">Ready to order your first container?</p>
            <a
              href="/#order-form"
              className="mt-5 inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-7 py-3 transition-colors"
            >
              Request a Quote
            </a>
          </div>
        </div>
      </article>
      <Footer />
    </div>
  );
}
